import { useState } from "react";
// AM-028 — VERIFY AGAINST repo: مسیر دقیق hookهای orval بعد از regenerate
// (orval باید بعد از اضافه‌شدن اندپوینت‌های جدید backend دوباره اجرا شود:
// `npx orval` طبق pishraft.md). نام‌های زیر فرضی و بر اساس الگوی سایر
// hookهای Family/Revision این پروژه (مثلاً useRoutingFamilyList) هستند.
import {
  useV1ProductionRoutingWorkInstructionFamiliesList as useWorkInstructionFamilyList,
  useV1ProductionRoutingWorkInstructionFamiliesCreate as useWorkInstructionFamilyCreate,
} from "../../../api/generated/v1/v1";

interface WorkInstructionFamilyListPageProps {
  routingOperationId: string;
  onSelectFamily?: (familyId: string) => void;
  selectedFamilyId?: string | null;
}

/**
 * AM-028 §2.1 — صفحهٔ مدیریت SOP در ماژول مهندسی Routing.
 * ساخت/ویرایش/نسخه‌بندی اینجا انجام می‌شود؛ صفحهٔ Shopfloor فقط Read-Only است.
 */
export function WorkInstructionFamilyListPage({
  routingOperationId,
  onSelectFamily,
  selectedFamilyId,
}: WorkInstructionFamilyListPageProps) {
  const [newCode, setNewCode] = useState("");

  const { data: families, isLoading, refetch } = useWorkInstructionFamilyList({
    routing_operation: routingOperationId,
  } as any);
  const createFamily = useWorkInstructionFamilyCreate();

  const handleCreate = () => {
    if (!newCode.trim()) return;
    createFamily.mutate({
      data: { code: newCode.trim(), routing_operation: routingOperationId },
    }, {
      onSuccess: (res: any) => {
        refetch();
        if (onSelectFamily && res?.id) onSelectFamily(res.id);
      }
    });
    setNewCode("");
  };

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-500">در حال بارگذاری...</div>;
  }

  return (
    <div
      style={{
        padding: "1.5rem",
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        marginTop: "1rem"
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#007270", margin: 0 }}>
          دستورالعمل‌های کاری (SOP)
        </h3>
      </div>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "2rem", alignItems: "center" }}>
        <input
          style={{
            flex: 1,
            padding: "0.75rem",
            borderRadius: "8px",
            border: "1px solid #D1D5DB",
            outline: "none",
            fontSize: "0.95rem"
          }}
          placeholder="کد جدید SOP (مثال: SOP-ASSY-01)"
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
        />
        <button
          type="button"
          onClick={handleCreate}
          disabled={createFamily.isPending || !newCode.trim()}
          style={{
            padding: "0.75rem 2rem",
            backgroundColor: "#007270",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "8px",
            fontWeight: 700,
            cursor: (createFamily.isPending || !newCode.trim()) ? "not-allowed" : "pointer",
            opacity: (createFamily.isPending || !newCode.trim()) ? 0.6 : 1,
            whiteSpace: "nowrap"
          }}
        >
          افزودن SOP جدید
        </button>
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {families?.results?.length ? (
          families.results.map((family: any) => (
            <li 
              key={family.id} 
              style={{
                padding: "1rem 1.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: family.id === selectedFamilyId ? "#F0FDF4" : "#F9FAFB",
                border: `1px solid ${family.id === selectedFamilyId ? "#86EFAC" : "#E5E7EB"}`,
                borderRadius: "8px",
                transition: "all 0.2s ease-in-out"
              }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1F2937", marginBottom: "0.25rem" }}>
                  {family.code}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#6B7280" }}>
                  تعداد نسخه‌ها: <span style={{ fontWeight: 600 }}>{family.revisions?.length ?? 0}</span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
                <span
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    padding: "0.25rem 0.75rem",
                    borderRadius: "9999px",
                    backgroundColor: family.revisions?.some((r: any) => r.status === "published") ? "#D1FAE5" : "#FEF3C7",
                    color: family.revisions?.some((r: any) => r.status === "published") ? "#065F46" : "#92400E"
                  }}
                >
                  {family.revisions?.some((r: any) => r.status === "published")
                    ? "منتشرشده"
                    : "بدون نسخهٔ منتشرشده"}
                </span>
                {onSelectFamily && (
                  <button
                    onClick={() => onSelectFamily(family.id)}
                    style={{
                      padding: "0.5rem 1.25rem",
                      backgroundColor: family.id === selectedFamilyId ? "#10B981" : "#E5E7EB",
                      color: family.id === selectedFamilyId ? "#FFFFFF" : "#374151",
                      border: "none",
                      borderRadius: "6px",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      transition: "all 0.2s"
                    }}
                  >
                    {family.id === selectedFamilyId ? "در حال ویرایش..." : "ویرایش دستورالعمل"}
                  </button>
                )}
              </div>
            </li>
          ))
        ) : (
          <li style={{ padding: "2rem", textAlign: "center", color: "#6B7280", backgroundColor: "#F9FAFB", borderRadius: "8px", border: "1px dashed #D1D5DB" }}>
            هنوز هیچ دستورالعملی برای این عملیات ثبت نشده است. از فرم بالا برای ساخت اولین دستورالعمل استفاده کنید.
          </li>
        )}
      </ul>
    </div>
  );
}

export default WorkInstructionFamilyListPage;
